import { useEffect, useRef, useState } from 'react';
import Button from '../../buttons/Button';
import Alert from '../../feedback/Alert';
import { extractDescriptor, loadFaceApi } from '@/utils/face';

export interface FaceCaptureResult { blob: Blob; dataUrl: string; descriptor: number[] | null }

/**
 * Captura de selfie pela câmera (C-24) com extração do descritor facial.
 * - `requireFace`: só aceita a foto se um rosto for detectado.
 * - `facing`: câmera frontal (user) ou traseira (environment, para fotografar documento).
 */
export default function FaceCapture({ onCapture, requireFace = true, facing = 'user', captureLabel = 'Tirar foto', round = true, autoStart = true, allowUpload = true }: {
  onCapture: (r: FaceCaptureResult) => void | Promise<void>;
  requireFace?: boolean;
  facing?: 'user' | 'environment';
  captureLabel?: string;
  round?: boolean;
  autoStart?: boolean;
  /** Permite escolher uma foto do aparelho (desligue no totem da cabine). */
  allowUpload?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<'idle' | 'starting' | 'ready' | 'busy' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  async function start() {
    setError(null);
    setPreview(null);
    setStatus('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      if (requireFace) await loadFaceApi();
      setStatus('ready');
    } catch (e) {
      setStatus('error');
      setError((e as Error).name === 'NotAllowedError'
        ? 'Permita o acesso à câmera nas configurações do navegador para continuar.'
        : 'Não foi possível abrir a câmera neste dispositivo.');
    }
  }

  function stop() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  useEffect(() => {
    if (autoStart) void start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Alternativa sem câmera: escolher uma foto do dispositivo (o rosto é detectado na imagem). */
  async function fromFile(file: File) {
    setStatus('busy');
    setError(null);
    const dataUrl = await new Promise<string>((r) => { const fr = new FileReader(); fr.onload = () => r(String(fr.result)); fr.readAsDataURL(file); });
    const img = new Image();
    img.src = dataUrl;
    await img.decode();
    const descriptor = requireFace ? await extractDescriptor(img) : null;
    if (requireFace && !descriptor) {
      setStatus('error');
      setError('Não encontramos um rosto nesta foto. Envie uma foto de frente, nítida e bem iluminada.');
      return;
    }
    stop();
    setPreview(dataUrl);
    await onCapture({ blob: file, dataUrl, descriptor });
    setStatus('idle');
  }

  async function capture() {
    const video = videoRef.current;
    if (!video) return;
    setStatus('busy');
    setError(null);
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d')!;
    if (facing === 'user') { ctx.translate(canvas.width, 0); ctx.scale(-1, 1); }
    ctx.drawImage(video, 0, 0);
    const descriptor = requireFace ? await extractDescriptor(canvas) : null;
    if (requireFace && !descriptor) {
      setStatus('ready');
      setError('Não encontramos seu rosto. Fique de frente para a câmera, com boa iluminação e sem óculos escuros.');
      return;
    }
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    const blob = await new Promise<Blob>((r) => canvas.toBlob((b) => r(b!), 'image/jpeg', 0.9));
    setPreview(dataUrl);
    stop();
    await onCapture({ blob, dataUrl, descriptor });
    setStatus('idle');
  }

  return (
    <div className="stack" style={{ alignItems: 'center' }}>
      <div className={round ? `selfie-frame ${preview ? 'ok' : ''}` : 'card flat'} style={round ? undefined : { width: '100%', aspectRatio: '4/3', display: 'grid', placeItems: 'center', background: 'var(--teal-soft)' }}>
        {preview ? <img src={preview} alt="Foto capturada" style={round ? undefined : { width: '100%', height: '100%', objectFit: 'cover', transform: 'none' }} />
          : <video ref={videoRef} muted playsInline style={round ? undefined : { width: '100%', height: '100%', objectFit: 'cover', transform: facing === 'user' ? 'scaleX(-1)' : 'none' }} />}
        {status === 'starting' && <span className="spinner lg" style={{ position: 'absolute' }} />}
      </div>
      {error && <Alert tone="warn">{error}</Alert>}
      {allowUpload && !preview && (
        <label className="btn link small" style={{ cursor: 'pointer' }}>
          <input type="file" accept="image/*" className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) void fromFile(f); }} />
          ou enviar uma foto do dispositivo
        </label>
      )}
      {preview ? (
        <Button variant="ghost" onClick={start}>Tirar outra foto</Button>
      ) : status === 'error' || status === 'idle' ? (
        <Button variant="ghost" onClick={start}>Abrir câmera</Button>
      ) : (
        <Button onClick={capture} loading={status === 'busy'} disabled={status !== 'ready'}>📷 {captureLabel}</Button>
      )}
    </div>
  );
}
